import React, { Component } from 'react';
import FormicaInput from '../FormicaInput';
import IntervalDropDown, { DurationUnit } from '../IntervalDropDown';
import { FormicaTimePickerDate } from './TimePickerDomain';
import moment from 'moment';
import FormicaToggleSwitch from '../FormicaToggleSwitch';
import LanguageUtil from '../../../util/LanguageUtil';

class CustomRelativeTimePicker extends Component<Props, State> {
  public state = new State();

  render() {
    const { durationValue, durationUnit, round } = this.state;
    const { type, roundVisible } = this.props;
    return (
      <div className={'flex flex-col gap-y-2 w-full px-2 py-2 gap-x-2'}>
        <div className={'flex gap-x-2'}>
          <FormicaInput
            value={durationValue}
            onChange={(e) => this.onChangeDurationValue(e.currentTarget.value)}
            type={'number'}
            data-testid={`date_time_picker_${type}_Relative_input`}
          />
          <IntervalDropDown
            onBlur={this.setDateWithDuration}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectDuration', defaultMessage: 'Select Duration' })}
            value={durationUnit}
            onChange={(e) => this.onChangeDurationUnit(e.value)}
            data-testid={`date_time_picker_${type}_Relative_select`}
          />
        </div>
        {roundVisible && (
          <div className={'flex gap-x-1 items-center'}>
            <FormicaToggleSwitch
              value={round}
              large={false}
              onChange={() => {
                const newRound = !this.state.round;
                this.setState({ round: newRound });
              }}
            />
            <span className={'text-sm'}>
              {LanguageUtil.get({ id: 'common.label.roundToThe', defaultMessage: 'Round to the ' })} {durationUnit}
            </span>
          </div>
        )}
        {type == 'Start Date' && (
          <div className={'flex flex-col w-full gap-y-4'}>
            <div className={'flex w-full h-full gap-x-5 items-center justify-between'}>
              <div className={'flex w-full h-[2px] bg-[#EEEEEE]'} />
              <div className={'text-f-black-2'}>{LanguageUtil.get({ id: 'common.label.or', defaultMessage: 'or' })}</div>
              <div className={'flex w-full h-[2px] rounded bg-[#EEEEEE]'} />
            </div>
            <a
              onClick={this.onClickSetBeginning}
              className={
                'flex items-center justify-center w-full h-10 bg-[#F0F4FF] text-primary rounded-[4px] cursor-pointer shrink-0 text-sm border-[0.5px] border-[#BECEFF]'
              }
            >
              {LanguageUtil.get({ id: 'common.label.setAsBeginning', defaultMessage: 'Set as Beginning' })}
            </a>
          </div>
        )}
        {type == 'End Date' && (
          <div className={'flex flex-col w-full gap-y-4'}>
            <div className={'flex w-full h-full gap-x-5 items-center justify-between'}>
              <div className={'flex w-full h-[2px] bg-[#EEEEEE]'} />
              <div className={'text-f-black-2'}>{LanguageUtil.get({ id: 'common.label.or', defaultMessage: 'or' })}</div>
              <div className={'flex w-full h-[2px] rounded bg-[#EEEEEE]'} />
            </div>
            <a
              onClick={this.setDateToNow}
              className={
                'flex items-center justify-center w-full h-10 bg-[#F0F4FF] text-primary rounded-[4px] cursor-pointer shrink-0 text-sm border-[0.5px] border-[#BECEFF]'
              }
              data-testid={`date_time_picker_${type}_Relative_now_button`}
            >
              {LanguageUtil.get({ id: 'common.label.setAsNow', defaultMessage: 'Set as Now' })}
            </a>
          </div>
        )}
      </div>
    );
  }

  public componentDidMount() {
    const { date } = this.props;
    if (date && date.type == 'Relative') {
      const labelParts = date.label?.split(' ') || [];
      if (labelParts.length === 3 && labelParts[2]?.toLowerCase() === 'ago') {
        const [durationValue, durationUnit] = labelParts;
        this.setState({
          durationValue: parseInt(durationValue),
          durationUnit: durationUnit as any,
        });
      }
      if (typeof date.round === 'boolean') {
        this.setState({ round: date.round });
      }
    }
  }

  public componentDidUpdate(_prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { durationValue, durationUnit, round } = this.state;
    if (isNaN(durationValue)) return;
    if (durationValue !== prevState.durationValue || durationUnit !== prevState.durationUnit || round !== prevState.round) {
      this.setDateWithDuration();
    }
  }

  private setDateToNow = () => {
    const { setDate } = this.props;
    setDate({ label: LanguageUtil.get({ id: 'common.label.now', defaultMessage: 'Now' }), value: new Date(), type: 'Now' });
  };

  private onChangeDurationValue = (value: number | string) => {
    const parsed = Number(value);
    this.setState({ durationValue: isNaN(parsed) ? 0 : parsed });
  };

  private onChangeDurationUnit = (value: DurationUnit) => {
    this.setState({ durationUnit: value });
  };

  private setDateWithDuration = () => {
    const { setDate } = this.props;
    const { durationValue, durationUnit, round } = this.state;
    const date = this.getDateBeforeDuration(new Date(), durationValue, durationUnit);
    setDate({
      label: LanguageUtil.get({ id: 'common.label.ago', defaultMessage: 'ago' }, { 0: durationValue, 1: durationUnit }),
      value: date,
      type: 'Relative',
      round,
    });
  };

  private getDateBeforeDuration(date: Date, durationValue: number, durationUnit: DurationUnit): Date {
    const durationInMs = durationValue * this.getDurationInMs(durationUnit);
    return new Date(date.getTime() - durationInMs);
  }

  private getDurationInMs(durationUnit: DurationUnit): number {
    const msInSecond = 1000;
    const msInMinute = msInSecond * 60;
    const msInHour = msInMinute * 60;
    const msInDay = msInHour * 24;
    const msInWeek = msInDay * 7;
    const msInMonth = msInDay * 30.44;
    const msInYear = msInDay * 365.24;

    switch (durationUnit) {
      case 'millisecond':
        return 1;
      case 'second':
        return msInSecond;
      case 'minute':
        return msInMinute;
      case 'hour':
        return msInHour;
      case 'day':
        return msInDay;
      case 'week':
        return msInWeek;
      case 'month':
        return msInMonth;
      case 'year':
        return msInYear;
      default:
        return 1;
    }
  }

  private onClickSetBeginning = () => {
    const { setDate } = this.props;
    const beginning = moment().subtract(5, 'year').add(1, 'day').toDate();
    setDate({ label: LanguageUtil.get({ id: 'common.label.beginning', defaultMessage: 'Beginngin' }), value: beginning, type: 'Absolute' });
  };
}

interface Props {
  date: FormicaTimePickerDate;
  setDate: (date: FormicaTimePickerDate) => void;
  type: 'Start Date' | 'End Date';
  isVelocity?: boolean;
  roundVisible?: boolean;
}

class State {
  durationValue: number = 1;
  durationUnit: DurationUnit = 'day';
  round: boolean = false;
}

export default CustomRelativeTimePicker;
